<template>
	<Head>
		<Link
			rel="preconnect"
			href="https://fonts.googleapis.com/"
		/>
		<Link
			rel="preconnect"
			href="https://fonts.gstatic.com"
		/>
		<Meta
			v-if="isProduction"
			name="robots"
			content="index, follow"
		/>
		<Meta
			v-else
			name="robots"
			content="noindex, nofollow"
		/>
	</Head>

	<ClientOnly>
		<SuccessToast
			v-if="showSuccessToast"
			ref="successToastRef"
			:message="successToastMessage"
			@closed="showSuccessToast = false"
		/>
	</ClientOnly>

	<!-- <LazyBreakingNews v-if="showBreakingNews" /> -->
	<stt-header class="bg-slate-500"></stt-header>

	<NuxtErrorBoundary>
		<!-- You use the default slot to render your content -->
		<template #error="{ error, clearError }">
			You can display the error locally here: {{ error }}
			<button @click="clearError">This will clear the error.</button>
		</template>
	</NuxtErrorBoundary>

	<slot />

	<div
		v-if="store.showSignUpModal"
		class="w-full max-w-[560px] shadow-lg bg-white z-[99] mx-auto p-[24px] left-0 bottom-[64px] right-0 rounded-[20px] fixed max-h-[360px] border-[3px] border-slate-100"
	>
		<button
			class="block ml-auto"
			@click="store.toggleSignUp()"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				height="24"
				viewBox="0 -960 960 960"
				width="24"
			>
				<path
					d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"
				/>
			</svg>
		</button>
		<div
			class="text-[32px] lg:text-[40px] text-slate-900 text-center black mb-[32px] leading-tight"
		>
			Get Daily, FREE Trade Alerts From Our Algorithm
		</div>

		<LeadCaptureForm
			id="sttwl-mainsite-bottom"
			class="max-w-[792px]"
			alt="true"
			:alt-hanlder="() => store.toggleSignUp()"
		/>
	</div>
	<SttFooter />
	<div
		v-if="$route.path !== '/1'"
		class="fixed bottom-0 left-0 right-0 z-[20]"
		id="cookie-consent"
	>
		<CookieConsent />
	</div>

	<LeadCaptureModal
		:is-open="store.showLeadCaptureModal"
		:show-validation-success="validationState.showSuccessMessage"
		:show-validation-error="validationState.showErrorMessage"
		:validation-errors="validationState.errorDetails"
		@close="store.toggleLeadCaptureModal"
	/>
	<dialog class="plc741013 adbutler-dialog modal-ad"></dialog>
	<button
		id="adbutler-modal-hidden-button"
		class="opacity-0 fixed bottom-0 right-0 z-1"
	>
		Show AdButler Modal
	</button>
</template>

<script setup>
import { onBeforeUnmount } from "vue";
import dayjs from "dayjs";

import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { CookieConsent } from "@stockstotrade_com/stt-ts-shared-lib";
import axios from "axios";
import { store, useSidebarStore } from "@/utils/store";
import { useStocksStore } from "@/stores/useStocksStore";

dayjs.extend(timezone);
dayjs.extend(utc);

const sidebarStore = useSidebarStore();

const $route = useRoute();
const { env, songbirdProxy, disable_adbutler_modal } =
	useRuntimeConfig().public;

const isProduction = env === "production";

const showAds = ref(false);
const showBreakingNews = ref(false);
const mostActiveStocksInterval = ref(null);
const adButlerObserver = ref(null);
const adButlerAutoCloseTimeout = ref(null);

// Utility functions for localStorage with expiration (5 days)
const setLocalStorageWithExpiry = (key, value, expiryDays = 5) => {
	if (import.meta.client) {
		const now = new Date();
		const item = {
			value,
			expiry: now.getTime() + expiryDays * 24 * 60 * 60 * 1000 // 5 days in milliseconds
		};
		localStorage.setItem(key, JSON.stringify(item));
	}
};

const getLocalStorageWithExpiry = (key) => {
	if (!import.meta.client) {
		return null;
	}

	const itemStr = localStorage.getItem(key);
	if (!itemStr) {
		return null;
	}

	try {
		const item = JSON.parse(itemStr);
		const now = new Date();

		// Check if item is expired
		if (now.getTime() > item.expiry) {
			localStorage.removeItem(key);
			return null;
		}

		return item.value;
	} catch (e) {
		// If parsing fails, treat as expired/invalid
		localStorage.removeItem(key);
		return null;
	}
};

// Success toast for post-redirect (after successful submission)
const showSuccessToast = ref(false);
const successToastMessage = ref("You have successfully subscribed!");
const successToastRef = ref(null);

// Access the global validation state from the XHR interceptor plugin
const validationState = useState("validation", () => ({
	isValidating: false,
	showSuccessMessage: false,
	showErrorMessage: false,
	errorDetails: {}
}));

const stocksStore = useStocksStore();

watch(
	() => $route.name,
	(newPath) => {
		if (newPath !== "blog-slug" && newPath !== "widgetpage") {
			sidebarStore.toggleContentSidebarVisibility(false);
			sidebarStore.setContentSidebarName(null);
			sidebarStore.setContentSidebarData(null);
		}
	}
);
const loadAccessibe = () => {
	const s = document.createElement("script");
	const h = document.querySelector("head") || document.body;
	s.src = "https://acsbapp.com/apps/app/dist/js/app.js";
	s.async = true;
	s.onload = function () {
		acsbJS.init({
			statementLink: "",
			footerHtml: "",
			hideMobile: false,
			hideTrigger: false,
			disableBgProcess: false,
			language: "en",
			position: "right",
			leadColor: "#146FF8",
			triggerColor: "#146FF8",
			triggerRadius: "50%",
			triggerPositionX: "right",
			triggerPositionY: "bottom",
			triggerIcon: "people",
			triggerSize: "bottom",
			triggerOffsetX: 20,
			triggerOffsetY: 20,
			mobile: {
				triggerSize: "small",
				triggerPositionX: "right",
				triggerPositionY: "bottom",
				triggerOffsetX: 10,
				triggerOffsetY: 10,
				triggerRadius: "20"
			}
		});
	};
	h.appendChild(s);
};

useHead(() => {
	const prod_payload = [];
	const meta_payload = [];

	meta_payload.push({
		name: "robots",
		content: env === "production" ? "index, follow" : "noindex, nofollow"
	});

	return {
		link: [
			{ rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
			{ rel: "manifest", href: "/site.webmanifest" },
			{
				rel: "icon",
				type: "image/png",
				sizes: "16x16",
				href: "/fav-16x16.png"
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32x32.png"
			},
			{ rel: "mask-icon", color: "#5BBAD5", href: "/safari-pinned-tab.svg" },
			{ rel: "icon", type: "apple-touch-icon", href: "/apple-touch-icon.png" }
		],
		script: prod_payload,
		meta: [...meta_payload]
	};
});

const isMarketOpen = () => {
	// Use a more robust timezone approach
	const now = dayjs().tz("America/New_York");

	// Validate that the timezone conversion worked
	if (!now.isValid()) {
		console.warn(
			"Invalid date from timezone conversion, falling back to local time"
		);
		return false;
	}

	const dayOfWeek = now.day(); // 0 = Sunday, 6 = Saturday

	// Markets are closed on weekends
	if (dayOfWeek === 0 || dayOfWeek === 6) {
		return false;
	}

	const hour = now.hour();
	const minute = now.minute();
	const currentTime = hour * 100 + minute;

	// Market hours: 9:30 AM - 4:00 PM ET (930 - 1600)
	return currentTime >= 930 && currentTime <= 1600;
};

watch(
	() => $route.name,
	async (newPage, oldPage) => {
		if (import.meta.client) {
			if (
				(newPage === "news" || newPage === "quote-stock") &&
				!["quote-stock", "news"].includes(oldPage)
			) {
				if (mostActiveStocksInterval.value) {
					clearInterval(mostActiveStocksInterval.value);
				}
				await getMostActiveStocks();
			}
		}

		if (newPage === "index" && isWithinTimeRanges()) {
			showBreakingNews.value = true;
		} else if (newPage !== "index" && showBreakingNews.value) {
			showBreakingNews.value = false;
		}
	}
);

// Enrich already-stored stocks with historic chart data (runs after store is already populated)
const fetchHistoricDataForMostActiveStocks = async (stocks) => {
	try {
		const historic_data_response = await axios.get(
			`${songbirdProxy}stocks/history`,
			{
				params: {
					symbol: Object.keys(stocks).join(","),
					range: "1D",
					fraction: true
				},
				timeout: 15000
			}
		);

		const results = historic_data_response.data?.results;
		if (!results) {
			console.warn(
				"[fetchHistoricData] No results in history response, skipping chart enrichment."
			);
			return;
		}

		const arr = Object.values(stocks);
		const payloadWithHistoric = arr.map((item) => {
			const prices = results[item.SYMBOL]?.map((item) => item.close);
			const labels = results[item.SYMBOL]?.map(
				(item) => item.startdatetime || item.time
			);

			return {
				...item,
				prices,
				labels
			};
		});

		// Convert payload array to object using SYMBOL as key
		const payloadObject = payloadWithHistoric.reduce((acc, stock) => {
			acc[stock.SYMBOL] = stock;
			return acc;
		}, {});

		sessionStorage.setItem("mostActiveStocks", JSON.stringify(payloadObject));
		sessionStorage.setItem("mostActiveStocksTimestamp", Date.now().toString());
		stocksStore.setMostActiveStocks(payloadObject);
	} catch (error) {
		console.warn(
			"[fetchHistoricData] Failed to fetch chart data, tickers remain without charts:",
			error?.message || error
		);
	}
};

const getMostActiveStocks = async (attempt = 1) => {
	const MAX_ATTEMPTS = 3;
	const RETRY_DELAY_MS = 3000;

	try {
		const most_active_stocks_promise = await axios.get(
			`${songbirdProxy}stocks/filtered`,
			{
				params: {
					value: "most_active",
					limit: 10
				},
				timeout: 15000
			}
		);

		if (most_active_stocks_promise.status === 200) {
			// Defensive programming: ensure we have an object, not an array
			let apiData = most_active_stocks_promise.data;

			if (Array.isArray(apiData)) {
				// Convert array to object using SYMBOL as key
				apiData = apiData.reduce((acc, item) => {
					if (item.SYMBOL) {
						acc[item.SYMBOL] = [item];
					}
					return acc;
				}, {});
			}

			const symbols = Object.keys(apiData);

			if (symbols.length > 0) {
				const res = await axios.get(`${songbirdProxy}stocks/quote`, {
					params: {
						symbol: symbols.join(",")
					},
					timeout: 15000
				});

				// Convert array to object using SYMBOL as key
				const stocksObject = res.data.data.reduce((acc, stock) => {
					acc[stock.SYMBOL] = stock;
					return acc;
				}, {});

				// Populate the store immediately so Trending Tickers appear without
				// waiting for the slower history/chart call below.
				stocksStore.setMostActiveStocks(stocksObject);

				// Fetch historic chart data in the background — does NOT block tickers.
				// If history fails, tickers are already visible; store is updated again
				// inside fetchHistoricDataForMostActiveStocks when history succeeds.
				fetchHistoricDataForMostActiveStocks(stocksObject);

				return () => clearInterval(mostActiveStocksInterval.value);
			}

			// API returned empty data — retry so transient upstream gaps don't persist
			if (attempt < MAX_ATTEMPTS) {
				console.warn(
					`[getMostActiveStocks] Empty response (attempt ${attempt}/${MAX_ATTEMPTS}), retrying in ${RETRY_DELAY_MS}ms...`
				);
				await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
				return getMostActiveStocks(attempt + 1);
			}

			console.warn(
				"[getMostActiveStocks] API returned empty data after all retries."
			);
		}
	} catch (error) {
		if (attempt < MAX_ATTEMPTS) {
			console.warn(
				`[getMostActiveStocks] Error on attempt ${attempt}/${MAX_ATTEMPTS}, retrying...`,
				error?.message || error
			);
			await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
			return getMostActiveStocks(attempt + 1);
		}
		console.error("Error getting [most_active_stocks_promise]:", error);
	}
};

onMounted(async () => {
	if (import.meta.client) {
		// Load prebid.js client-only so failures (404, ad blocker) don't block or break direct load
		const prebid = document.createElement("script");
		prebid.src = "/js/prebid.js";
		prebid.defer = true;
		prebid.onerror = () => {}; // noop so failed load doesn't throw
		document.body.appendChild(prebid);

		// Check for success flag and show toast if present
		if (typeof window !== "undefined" && window.localStorage) {
			const successFlag = window.localStorage.getItem("stt_submission_success");

			if (successFlag === "true") {
				// Clear the flag
				window.localStorage.removeItem("stt_submission_success");

				// Close the modal if it's open
				if (store.showLeadCaptureModal) {
					store.toggleLeadCaptureModal();
				}

				// Show toast after page is fully loaded
				setTimeout(() => {
					showSuccessToast.value = true;
					nextTick(() => {
						successToastRef.value?.open();
					});
				}, 500);
			}
		}

		// ---------------------------------------------------------------
		// Fire stock data fetching independently (non-blocking).
		// These calls may be slow on serverless (Netlify) and must NOT
		// block page setup (Accessibe, AdButler, breaking news, etc.).
		// ---------------------------------------------------------------
		const fetchMostActiveStocksData = async () => {
			try {
				if (
					sessionStorage.getItem("mostActiveStocksTimestamp") &&
					sessionStorage.getItem("mostActiveStocksTimestamp") >
						Date.now() - 1000 * 60 * 60
				) {
					const stocks_data = JSON.parse(sessionStorage.getItem("mostActiveStocks"));
					stocksStore.setMostActiveStocks(stocks_data);

					mostActiveStocksInterval.value = setInterval(async () => {
						// Defensive programming: ensure we have an object, not an array
						let stocksDataForInterval = stocks_data;
						if (Array.isArray(stocksDataForInterval)) {
							console.warn(
								"⚠️ [onMounted interval] Session storage data is array instead of object, converting..."
							);
							stocksDataForInterval = stocksDataForInterval.reduce((acc, item) => {
								if (item.SYMBOL) {
									acc[item.SYMBOL] = [item];
								}
								return acc;
							}, {});
						}

						const symbols = Object.keys(stocksDataForInterval);

						if (symbols.length > 0) {
							try {
								const res = await axios.get(`${songbirdProxy}stocks/quote`, {
									params: {
										symbol: Object.keys(stocksDataForInterval).join(",")
									},
									timeout: 15000
								});

								if (res.data?.data) {
									const stocksObject = res.data.data.reduce((acc, stock) => {
										acc[stock.SYMBOL] = stock;
										return acc;
									}, {});

									// Only update current data, preserve existing historic data (charts)
									stocksStore.updateMostActiveStocksCurrentData(stocksObject);
								}
							} catch (error) {
								console.warn(
									"[default.vue] Failed to refresh most active stocks:",
									error?.message || error
								);
							}
						}
					}, 1000 * 10);
				} else {
					await getMostActiveStocks();
				}
			} catch (error) {
				console.warn(
					"[default.vue] Error in most active stocks flow:",
					error?.message || error
				);
			}
		};

		const fetchTopGainersData = async () => {
			try {
				if (
					sessionStorage.getItem("topGainerStocksTimestamp") &&
					sessionStorage.getItem("topGainerStocksTimestamp") >
						Date.now() - 1000 * 60 * 60 * 24
				) {
					stocksStore.setTopGainerStocks(
						JSON.parse(sessionStorage.getItem("topGainerStocks"))
					);
				}

				const top_gainers_promise = await axios.get(
					`${songbirdProxy}stocks/news/filtered`,
					{
						params: {
							value: "top_gainers",
							is_sector: false
						},
						timeout: 15000
					}
				);

				if (top_gainers_promise?.status === 200) {
					// Defensive programming: ensure we have an object, not an array
					let topGainersData = top_gainers_promise.data;

					if (Array.isArray(topGainersData)) {
						console.warn(
							"⚠️ [onMounted] Top gainers API returned array instead of object, converting..."
						);
						// Convert array to object using SYMBOL as key
						topGainersData = topGainersData.reduce((acc, item) => {
							if (item.SYMBOL) {
								acc[item.SYMBOL] = [item];
							}
							return acc;
						}, {});
					}

					const symbols = Object.keys(topGainersData);

					if (symbols.length > 0) {
						// Fetch quote and history data in parallel instead of sequentially
						const [current_data_response, historic_data_response] = await Promise.all(
							[
								axios.get(`${songbirdProxy}stocks/quote`, {
									params: {
										symbol: symbols.join(",")
									},
									timeout: 15000
								}),
								axios.get(`${songbirdProxy}stocks/history`, {
									params: {
										symbol: symbols.join(","),
										range: "1D",
										fraction: true
									},
									timeout: 15000
								})
							]
						);

						const results = historic_data_response.data?.results;

						if (results && current_data_response.data?.data) {
							// Convert current_data_response.data.data to object keyed by SYMBOL
							const currentDataObject = current_data_response.data.data.reduce(
								(acc, stock) => {
									acc[stock.SYMBOL] = stock;
									return acc;
								},
								{}
							);

							// Build payload in the order of symbols array (which matches results order)
							const payload = symbols
								.map((symbol) => {
									const item = currentDataObject[symbol];
									if (!item) {
										return null;
									}

									const prices = results[symbol]?.map((item) => item.close) || [];
									const labels =
										results[symbol]?.map((item) => item.startdatetime || item.time) || [];

									return {
										...item,
										prices,
										labels
									};
								})
								.filter(Boolean) // Remove any null entries
								.sort((a, b) => (b.PC_CHG || 0) - (a.PC_CHG || 0)); // Sort by PC_CHG descending (highest gain first)

							// Save top gainers to session storage
							sessionStorage.setItem("topGainerStocks", JSON.stringify(payload));
							sessionStorage.setItem(
								"topGainerStocksTimestamp",
								Date.now().toString()
							);

							stocksStore.setTopGainerStocks(payload);
						}

						// Set up interval to refetch fresh data every 60 seconds
						if (isMarketOpen()) {
							const topGainersInterval = setInterval(async () => {
								// Check if markets are still open before making API calls
								if (!isMarketOpen()) {
									console.log("Markets closed, stopping top gainers updates");
									clearInterval(topGainersInterval);
									return;
								}

								try {
									// Fetch fresh quote and history data in parallel
									const [freshCurrentData, freshHistoricData] = await Promise.all([
										axios.get(`${songbirdProxy}stocks/quote`, {
											params: {
												symbol: symbols.join(",")
											},
											timeout: 15000
										}),
										axios.get(`${songbirdProxy}stocks/history`, {
											params: {
												symbol: symbols.join(","),
												range: "1D",
												fraction: true
											},
											timeout: 15000
										})
									]);

									if (
										freshCurrentData?.data?.data &&
										freshCurrentData.data.data.length > 0
									) {
										const freshResults = freshHistoricData.data?.results;
										const freshPayload = freshCurrentData.data.data
											.map((item) => {
												return {
													...item,
													prices: freshResults?.[item.SYMBOL]?.map((p) => p.close) || [],
													labels:
														freshResults?.[item.SYMBOL]?.map(
															(p) => p.time || p.startdatetime
														) || []
												};
											})
											.sort((a, b) => (b.PC_CHG || 0) - (a.PC_CHG || 0));

										// Update session storage with fresh data
										sessionStorage.setItem(
											"topGainerStocks",
											JSON.stringify(freshPayload)
										);
										sessionStorage.setItem(
											"topGainerStocksTimestamp",
											Date.now().toString()
										);

										// Update the store with fresh data
										stocksStore.setTopGainerStocks(freshPayload);
									}
								} catch (error) {
									console.error("Error refreshing top gainers data:", error);
								}
							}, 10000); // 10 seconds

							// Store interval ID for cleanup
							stocksStore.topGainerStocksIntervalId = topGainersInterval;
						} else {
							console.log("Markets closed, not starting top gainers updates");
						}
					}
				}
			} catch (error) {
				console.warn(
					"[default.vue] Failed to fetch top gainers:",
					error?.message || error
				);
			} finally {
				stocksStore.top_gainers_loaded = true;
			}
		};

		await fetchMostActiveStocksData();
		await fetchTopGainersData();
	}

	// ---------------------------------------------------------------
	// Critical page setup — must always run regardless of API health
	// ---------------------------------------------------------------
	if ($route.name === "index") {
		const isInTimeRange = isWithinTimeRanges();
		if (import.meta.client && isInTimeRange) {
			setTimeout(() => {
				showBreakingNews.value = true;
			}, 1000);
		}
	}

	// Skip AdButler logic on test page /1
	if (import.meta.client && window.adsAreWithUs && $route.path !== "/1") {
		showAds.value = true;

		setTimeout(() => {
			const adbutler_link = document.querySelectorAll(".watch-live-stt-header a");

			if (adbutler_link) {
				adbutler_link.forEach((item) => item.setAttribute("target", "_blank"));
			}
		}, 1000);
	}

	if (import.meta.client) {
		if (env == "production") {
			loadAccessibe();
		}

		if (disable_adbutler_modal === "true") {
			return;
		}

		// Function to add AdButler initialization code to bottom of page
		const addAdButlerInitializationCode = () => {
			// Check if modal should be disabled
			const modalPreference = getLocalStorageWithExpiry("show-adbutler-modal");
			if (modalPreference === "false") {
				return; // Don't initialize if preference is false
			}

			// Check if initialization code already exists
			const existingInit = document.querySelector("script[data-adbutler-init]");
			if (existingInit) {
				return; // Already added
			}

			const initScript = document.createElement("script");
			initScript.type = "text/javascript";
			initScript.setAttribute("data-adbutler-init", "true");
			initScript.textContent = `
				var AdButler = AdButler || {}; AdButler.ads = AdButler.ads || [];
				var abkw = window.abkw || '';
				var plc741013 = window.plc741013 || 0;
				(function () {
					var divs = document.querySelectorAll(".plc741013:not([id])");
					var div = divs[divs.length - 1];
					div.id = "placement_741013_" + plc741013;
					AdButler.ads.push({ handler: function (opt) { AdButler.register(181458, 741013, [0, 0], 'placement_741013_' + opt.place, opt); }, opt: { place: plc741013++, keywords: abkw, domain: 'servedbyadbutler.com', click: 'CLICK_MACRO_PLACEHOLDER' } });
				})();
			`;
			document.body.appendChild(initScript);

			// Set up close button handler and auto-close after dialog opens
			setTimeout(() => {
				const dialog = document.querySelector(".adbutler-dialog");
				if (!dialog) {
					return;
				}

				// Set up close button click handler (prevent duplicate listeners)
				const setupCloseButton = () => {
					const closeButton = document.querySelector(".adblutler-modal-close");
					if (closeButton && !closeButton.hasAttribute("data-listener-attached")) {
						closeButton.setAttribute("data-listener-attached", "true");
						closeButton.addEventListener("click", () => {
							hideModal();
						});
					}
				};

				// Try to set up close button immediately
				setupCloseButton();

				// Also watch for when the dialog opens and set up close button then
				const dialogObserver = new MutationObserver(() => {
					if (dialog.open) {
						setupCloseButton();

						// Auto-close after 25 seconds when dialog opens
						if (adButlerAutoCloseTimeout.value) {
							clearTimeout(adButlerAutoCloseTimeout.value);
						}
						adButlerAutoCloseTimeout.value = setTimeout(() => {
							hideModal();
						}, 25000);
					}
				});

				dialogObserver.observe(dialog, {
					attributes: true,
					attributeFilter: ["open"]
				});

				// Set up click handler for hidden button
				const hiddenButton = document.getElementById(
					"adbutler-modal-hidden-button"
				);
				if (hiddenButton) {
					hiddenButton.onclick = () => {
						dialog.showModal();
					};

					setTimeout(() => {
						hiddenButton.click();
					}, 5000);
				}
			}, 1000);
		};

		// Mutation observer to check for AdButler script
		const checkAndAddAdButlerScript = () => {
			// Double-check before adding script
			const modalPreference = getLocalStorageWithExpiry("show-adbutler-modal");
			if (modalPreference === "false") {
				return; // Don't load if preference is false
			}

			const scriptUrl = "https://servedbyadbutler.com/app.js";
			const scripts = document.querySelectorAll('script[src="' + scriptUrl + '"]');

			// Check if script already exists
			if (scripts.length === 0 && !window.AdButler) {
				// Script not present, add it
				const s = document.createElement("script");
				s.async = true;
				s.type = "text/javascript";
				s.src = scriptUrl;
				s.onerror = () => {}; // Prevent failed load (blocker, network) from throwing
				const n = document.getElementsByTagName("script")[0];
				n.parentNode.insertBefore(s, n);

				// Wait for script to load, then add initialization code
				s.onload = () => {
					addAdButlerInitializationCode();
				};
			} else if (window.AdButler) {
				// Script already loaded, add initialization code
				addAdButlerInitializationCode();
			}
		};

		// Set up mutation observer on body
		adButlerObserver.value = new MutationObserver((_mutations) => {
			checkAndAddAdButlerScript();
		});

		// Start observing body for changes
		if (document.body) {
			adButlerObserver.value.observe(document.body, {
				childList: true,
				subtree: true
			});

			// Initial check
			checkAndAddAdButlerScript();
		} else {
			// Wait for body to be available
			document.addEventListener("DOMContentLoaded", () => {
				adButlerObserver.value.observe(document.body, {
					childList: true,
					subtree: true
				});
				checkAndAddAdButlerScript();
			});
		}
	}
});

// Cleanup intervals on component unmount
onBeforeUnmount(() => {
	if (mostActiveStocksInterval.value) {
		clearInterval(mostActiveStocksInterval.value);
		mostActiveStocksInterval.value = null;
	}
	if (adButlerObserver.value) {
		adButlerObserver.value.disconnect();
		adButlerObserver.value = null;
	}
	if (adButlerAutoCloseTimeout.value) {
		clearTimeout(adButlerAutoCloseTimeout.value);
		adButlerAutoCloseTimeout.value = null;
	}
});

const hideModal = () => {
	// Set show-adbutler-modal to false with 5-day expiration
	setLocalStorageWithExpiry("show-adbutler-modal", "false", 5);

	// Close the dialog if it's open
	if (import.meta.client) {
		const dialog = document.querySelector(".adbutler-dialog");
		if (dialog && dialog.open) {
			dialog.close();
		}
	}

	// Clear auto-close timeout if it exists
	if (adButlerAutoCloseTimeout.value) {
		clearTimeout(adButlerAutoCloseTimeout.value);
		adButlerAutoCloseTimeout.value = null;
	}
};
</script>

<style lang="scss">
@use "~/assets/utils.scss" as *;
@use "../assets/colors" as *;
@use "../assets/mixins" as *;
#header .plc750284 {
	@include max-breakpoint(lg) {
		display: none;
	}
}
.additional-info {
	display: flex;
	justify-content: space-between;
	align-items: center;
	a.post-tile:last-child {
		// display: none;
	}
	position: relative;
	p {
		position: absolute;
		right: 0;
		bottom: 0;
		top: 0;
		margin: auto;
		height: 16px;
	}
}

span.tim-bohen-thing {
	display: flex;
	gap: 8px;
}

.tim-bohen-pic {
	width: 34px;
	height: 34px;
	background-image: url("https://secure.gravatar.com/avatar/ffab25dfa1b032865bcfe0a47dde563a?s=48&d=mm&r=g");
	content: "";
	position: relative;
	display: block;
	background-size: cover;
	border-radius: 24px;
}
.modal-ad:before {
	display: none;
}

.adblutler-modal-close {
	height: 20px;
	color: #fff;
}
.adblutler-modal-close::after {
	position: absolute;
	z-index: 99;
	content: "";
	width: 20px;
	height: 20px;
	background-image: url("/images/close.svg");
	background-size: contain;
	top: 0;
	right: 0;
	bottom: 0;
	margin: auto;
}
.cookie-consent {
	background-color: rgb(5 41 143);
	border-bottom-color: $blue-300;
	z-index: 999;
}
.swiper-slide p {
	font-size: 15.2px;
}

.swiper-slide-button.prev {
	left: -36px;
	top: -52px;
}
.swiper-slide-button.next {
	right: -36px;
	top: -52px;
}

.testimonial-text .testimonial-author {
	font-size: 20px;
}
.testimonial-text h5 {
	font-size: 10.5px;
}
@media (min-width: 768px) {
	.swiper-slide p {
		font-size: 19px;
	}

	.swiper-slide-button.prev {
		left: 0;
		top: -32px;
	}
	.swiper-slide-button.next {
		right: 0;
		top: -32px;
	}
}

.saswp-rvw-str {
	display: inline-flex;
	width: 100%;
}

.str-ic {
	font-size: 18px;
	line-height: 1.2;
}

.saswp-rvw-str .str-ic {
	background-image: url(data:image/svg+xml;utf8;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pgo8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMTkuMC4wLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogNi4wMCBCdWlsZCAwKSAgLS0+CjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiBpZD0iTGF5ZXJfMSIgeD0iMHB4IiB5PSIwcHgiIHZpZXdCb3g9IjAgMCA0ODIuMjA3IDQ4Mi4yMDciIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDQ4Mi4yMDcgNDgyLjIwNzsiIHhtbDpzcGFjZT0icHJlc2VydmUiIHdpZHRoPSIxNnB4IiBoZWlnaHQ9IjE2cHgiPgo8cG9seWdvbiBwb2ludHM9IjQ4Mi4yMDcsMTg2Ljk3MyAzMjIuNTA4LDE1My4yNjkgMjQxLjEwNCwxMS44MDMgMTU5LjY5OSwxNTMuMjY5IDAsMTg2Ljk3MyAxMDkuMzg4LDMwOC4xMDggOTIuMDk0LDQ3MC40MDQgICAyNDEuMTA0LDQwMy44MDMgMzkwLjExMyw0NzAuNDA0IDM3Mi44MTgsMzA4LjEwOCAiIGZpbGw9IiNmZmQ3MDAiLz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPGc+CjwvZz4KPC9zdmc+Cg==);
}

.saswp-rvw-str .df-clr,
.saswp-rvw-str .half-str,
.saswp-rvw-str .str-ic {
	display: inline-block;
	width: 20px;
	height: 16px;
	background-repeat: no-repeat;
}

.single-post #commentform {
	padding-top: 2rem;
}

.saswp-rating-container {
	position: relative;
}

.jq-ry-container {
	position: relative;
	padding: 0 5px;
	line-height: 0;
	display: block;
	cursor: pointer;
	-webkit-box-sizing: content-box;
	-moz-box-sizing: content-box;
	box-sizing: content-box;
	direction: ltr;
}

.jq-ry-container > .jq-ry-group-wrapper {
	position: relative;
	width: 100%;
}
.jq-ry-container > .jq-ry-group-wrapper > .jq-ry-group.jq-ry-normal-group {
	width: 100%;
}
.jq-ry-container > .jq-ry-group-wrapper > .jq-ry-group {
	position: relative;
	line-height: 0;
	z-index: 10;
	white-space: nowrap !important;
}

.jq-ry-container > .jq-ry-group-wrapper > .jq-ry-group > svg {
	display: inline-block;
}

#toc_container li,
#toc_container ul {
	margin: 0;
	padding: 0;
}
#toc_container.no_bullets li,
#toc_container.no_bullets ul,
#toc_container.no_bullets ul li,
.toc_widget_list.no_bullets,
.toc_widget_list.no_bullets li {
	background: 0 0;
	list-style-type: none;
	list-style: none;
}
#toc_container.have_bullets li {
	padding-left: 12px;
}
#toc_container ul ul {
	margin-left: 1.5em;
}
#toc_container {
	padding: 10px;
	margin-bottom: 1em;
	width: auto;
	display: table;
	font-size: 95%;

	background: linear-gradient(110.32deg, #091d56 8.05%, #331290 77.58%);
	color: white;
	padding: 20px;
	border-radius: 12px;
}
#toc_container.toc_light_blue {
	background: #edf6ff;
}
#toc_container.toc_white {
	background: #fff;
}
#toc_container.toc_black {
	background: #000;
}
#toc_container.toc_transparent {
	background: none transparent;
}
#toc_container p.toc_title {
	text-align: center;
	font-weight: 700;
	margin: 0;
	padding: 0;
	color: white;
	padding: 0 8px;
}
#toc_container.toc_black p.toc_title {
	color: #aaa;
}
#toc_container.contracted .toc_list {
	display: none;
	transition: all 250ms ease-in-out;
}

.toc_list li a:not(.toc_list li ul li a) {
	font-weight: bold;
}
.toc_fake_link {
	color: #3295fb;
}

#toc_container span.toc_toggle {
	font-weight: 400;
	font-size: 90%;
	margin-left: 8px;
	cursor: pointer;
}
#toc_container:not(.contracted) p.toc_title + ul.toc_list {
	margin-top: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;

	ul {
		margin-top: 16px;
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
}
#toc_container .toc_number {
	color: $blue-300;
}

#toc_container:not(.contracted) {
	max-width: 520px;
}
.toc_wrap_left {
	float: left;
	margin-right: 10px;
}
.toc_wrap_right {
	float: right;
	margin-left: 10px;
}
#toc_container a {
	text-decoration: none;
	text-shadow: none;
	color: $white;
}
#toc_container a:hover {
	text-decoration: underline;
}
.toc_sitemap_posts_letter {
	font-size: 1.5em;
	font-style: italic;
}

.end-of-article .related-post-info .read-more-link {
	font-size: 18px;
	margin-top: 20px;
	max-width: fit-content;
	align-items: center;
	position: relative;
	color: $blue-300;
	line-height: normal;
	&::after {
		content: "";
		background-image: url("/images/icons/external-link.svg");
		width: 14px;
		height: 14px;
		display: inline-block;
		background-size: contain;
		position: relative;
		left: -4px;
	}

	&:hover {
		// background-color: #eab308;
		// color: #1e3a8a;
		text-decoration: underline;
		&::after {
			background-image: url("/images/icons/external-link-hover.svg");
		}
	}
}

.end-of-article .related-post-info p {
	@include dmSans;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 28px;
	color: $slate-900;
	margin-bottom: 24px;
	a {
		color: $blue-300;
		&:hover {
			text-decoration: underline;
		}
	}

	@include breakpoint(lg) {
		font-size: 18px;
		margin-bottom: 28px;
	}

	img.aligncenter {
		margin: auto;
	}
}

.end-of-article .related-post-info h6 {
	color: $slate-800;

	/* Heading/H2 */
	font-size: 24px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%;
	margin-bottom: 32px;

	/* 45.6px */
	letter-spacing: -0.76px;

	@include breakpoint(lg) {
		font-size: 32px;
	}
}
.end-of-article .related-post-info p a {
	font-size: 18px;
	display: revert-layer;
	align-items: center;
	position: relative;
	color: $blue-300;
	line-height: normal;
	&::after {
		content: "";
		background-image: url("/images/icons/external-link.svg");
		width: 14px;
		height: 14px;
		display: inline-block;
		background-size: contain;
		margin-left: 4px;
	}

	&:hover {
		text-decoration: underline;
	}
}

.watch-live-stt-header a:not(.blob) {
	@include max-breakpoint(lg) {
		display: none;
	}
}

.watch-live-stt-header {
	@include max-breakpoint(lg) {
		height: 38px !important;
	}
}
.watch-live-stt-header .blob {
	@include max-breakpoint(lg) {
		margin-right: 32px !important;
	}
	@include breakpoint(lg) {
		margin-right: -38px;
		z-index: 99;
		position: relative;
		margin-left: 20px !important;
	}
}

#author-page .row {
	@include max-breakpoint(lg) {
		padding-left: 20px;
		padding-right: 20px;
	}
}

#author-page li {
	font-size: 1.1rem;
	margin-bottom: 1.15rem;
	padding-left: 16px;
	position: relative;
}
#author-page li:before {
	background-color: #3094fb;
	content: "";
	height: 8px;
	left: 0;
	position: absolute;
	top: 0.5rem;
	width: 8px;
}
#author-page .author-highlights a:after {
	background-image: url(../images/icons/external-link.svg);
	background-size: contain;
	content: "";
	display: inline-block;
	height: 14px;
	margin-left: 2px;
	width: 14px;
}
#author-page .author-highlights a:hover {
	background-color: #eab308;
	color: #1e3a8a;
	text-decoration: underline;
}
#author-page .author-highlights a:hover:after {
	background-image: url(../images/icons/external-link-hover.svg);
}
#author-page .author-socialmedia {
	display: flex;
	gap: 12px;
	margin-top: 20px;
}

.infusion-form {
	display: flex;
	flex-direction: column;
	row-gap: 24px;
}
.infusion-field {
	display: flex;
	flex-direction: column;

	label {
		color: $slate-900;
		font-weight: 600;
		margin-bottom: 12px;
	}

	input {
		border-radius: 8px;
		border: 1px solid $slate-100;
		height: 40px;
		background-color: #f6f8fa;
	}
	textarea {
		border-radius: 8px;
		border: 1px solid $slate-100;
		background-color: #f6f8fa;
	}
}

.infusion-submit input[type="submit"] {
	cursor: pointer;

	background: linear-gradient(110.32deg, #091d56 8.05%, #331290 77.58%);
	color: white;
	width: 100%;
	border-radius: 12px;
	transition: all 250ms ease-in-out;
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 46px;
}

.subscribers-modal.subscribers-modal-bottom-center {
	@include max-breakpoint(md) {
		max-width: calc(100% - 32px) !important;
	}
}

#referenced-stocks-sidebar {
	hr {
		display: none;
	}
}

.referenced-symbols {
	border-radius: 24px;
	background-color: white;
	border: 1px solid #e5e7eb;
	.overlay-arrow {
		@include max-breakpoint(lg) {
			display: none;
		}
	}
}

.post-table-style {
	td:nth-child(3) {
		width: 232px !important;
		text-align: left;
		margin-left: auto;
		display: block;
	}
}

h3 a[data-stock],
span a[data-stock],
h2 a[data-stock],
p a[data-stock],
b a[data-stock],
li a[data-stock] {
	color: $blue-300;
	text-decoration: underline;

	&:hover {
		background-color: yellow;
	}
}

.text-negative {
	color: $red-500;
}

// Modal Close Button
.adblutler-modal-close {
	cursor: pointer;
	margin-bottom: 16px;
	margin-left: auto;
	display: block;
	max-width: 40px;
	position: relative;
	text-align: right;
	@include breakpoint(lg) {
		margin-bottom: 24px;
	}
}

// Modal Overlay (backdrop)
.ad-modal-overlay {
	position: absolute;
	height: 100%;
	width: 100%;
	left: 0;
	top: 0;
	z-index: 0;
}

// Main Modal Content
.modal-ad {
	min-width: 320px;
	min-height: 200px;
	max-width: 500px;
	padding: 20px 24px 40px;
	overflow: hidden;
	overflow-y: auto;
	border-radius: 12px;
	background-color: white;
	position: fixed;
	z-index: 1;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	margin: auto 12px;
	border-bottom: 24px solid #1e3a8a !important;
	/* Hide scrollbars for modal */
	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* IE and Edge */
	overflow-y: scroll;
	&::-webkit-scrollbar {
		display: none; /* Chrome, Safari and Opera */
	}

	@include breakpoint(md) {
		margin: auto;
	}

	@include breakpoint(lg) {
		border: none;
		box-shadow: 0 0 8px rgba(0, 0, 0, 0.25);
		padding: 20px 32px 40px;
	}

	// Bottom center circle decoration
	&::after {
		content: "";
		height: 32px;
		width: 32px;
		display: block;
		background-size: contain;
		position: absolute;
		left: 0;
		right: 0;
		bottom: 8px;
		outline: 4px solid #1e3a8a;
		border-radius: 32px;
		margin: auto;
	}

	iframe {
		height: 100%;
		width: 100%;
	}

	.mb-5 {
		margin-bottom: 32px;
	}

	img {
		width: 100%;
		border-radius: 12px;
	}
}

// Modal Title Styling
.modal-title {
	text-align: center;
	font-size: 20px;
	margin-bottom: 23px;
	font-weight: 700;
	@include breakpoint(lg) {
		font-size: 28px;
		margin-bottom: 24px;
	}
}

// Modal Container (full screen overlay)
.modal-ad-container {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	margin: auto;
	z-index: 9999;
	display: flex;
	padding: 64px 16px;
	background-color: rgba(#fff, 0.65);
	overflow: auto;

	.information-subscribe-button {
		padding-top: 12px;
		padding-bottom: 12px;
		font-size: 18px;
	}
}

dialog::backdrop {
	background: rgba(255, 255, 255, 0.7);
}
</style>
